/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi, UploadApi } from '@alfresco/js-api';
import { defer, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

/** QName of the aspect that carries the enterprise DMS metadata. */
export const DMS_ASPECT = 'dms:documentInfo';

/** One selectable value of a LIST-constrained property, with its Thai label. */
export interface DmsOption {
  value: string;
  label: string;
}

/** A single property of {@link DMS_ASPECT}, as the repository defines it. */
export interface DmsField {
  id: string;
  dataType: string;
  mandatory: boolean;
  defaultValue?: string;
  /** Present only for LIST-constrained properties. */
  options?: DmsOption[];
}

export interface DmsSchema {
  fields: Record<string, DmsField>;
  /** True when `dms:documentInfo` is deployed and active in the repository. */
  available: boolean;
}

/**
 * Thai labels for the constrained values.
 *
 * The **allowed values themselves come from the repository** (the LIST constraints
 * on the aspect); this map only supplies the display text, so adding a value to the
 * model surfaces it in the UI immediately — untranslated, as its raw code, rather
 * than silently disappearing.
 */
const VALUE_LABELS: Record<string, string> = {
  // field ids — extra fields are labelled through the same map
  'dms:amount': 'DMS.UI.AMOUNT',

  // dms:documentType
  contract: 'DMS.UI.5AB4A2',
  invoice: 'DMS.UI.D8FEC4',
  'internal-memo': 'DMS.UI.646CE9',
  'meeting-minutes': 'DMS.UI.7BCF24',
  report: 'DMS.UI.41C825',
  policy: 'DMS.UI.DEC645',
  proposal: 'DMS.UI.934221',
  quotation: 'DMS.UI.44593B',
  form: 'DMS.UI.0A9A9F',
  certificate: 'DMS.UI.5DDB29',
  presentation: 'DMS.UI.73F3F6',
  other: 'DMS.UI.9322F6',

  // dms:status
  draft: 'DMS.UI.56A4F1',
  pending: 'DMS.UI.31BCD0',
  approved: 'DMS.UI.2DAAE3',
  rejected: 'DMS.UI.1A3B6C',
  expired: 'DMS.UI.DBCA12',
  archived: 'DMS.UI.152DA4',

  // dms:confidentiality
  public: 'DMS.UI.3EE1BE',
  internal: 'DMS.UI.D83B12',
  confidential: 'DMS.UI.0E6CDC',
  secret: 'DMS.UI.0C80FA'
};

/**
 * Names given to types added from the settings screen, read from
 * `Data Dictionary/ACA/document-types.json`. They are finished text rather than
 * translation keys, which the `translate` pipe passes through untouched, so a
 * caller needs to know nothing about where a label came from.
 */
let CUSTOM_LABELS: Record<string, string> = {};

/** Where the labels for types added from the settings screen are kept. */
const LABELS_FILE = 'document-types.json';

/**
 * Reads the enterprise DMS metadata contract from Alfresco and writes documents
 * back to it.
 *
 * The schema is **not** duplicated in the frontend: field list, data types,
 * defaults and allowed values are all read from `GET /aspects/dms:documentInfo`,
 * so the deployed content model (`server-extension/content-model/dmsModel.xml`)
 * stays the single source of truth. If the model is not deployed, {@link schema}
 * reports `available: false` and the caller can degrade honestly instead of
 * offering fields the repository would reject.
 */
@Injectable({ providedIn: 'root' })
export class DmsMetadataService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private schema$?: Observable<DmsSchema>;
  private labels$?: Observable<Record<string, string>>;

  private get uploadApi(): UploadApi {
    return new UploadApi(this.api.getInstance() as any);
  }

  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  /** The deployed metadata contract; cached for the lifetime of the session. */
  schema(): Observable<DmsSchema> {
    if (!this.schema$) {
      // the labels are fetched alongside, so every screen that reads the schema
      // already has the names of any type added from the settings screen
      this.schema$ = forkJoin({
        aspect: this.call(`/aspects/${DMS_ASPECT}`, 'GET'),
        labels: this.customLabels()
      }).pipe(
        map(({ aspect }) => this.toSchema(aspect?.entry?.properties ?? [])),
        // a missing/inactive model is a capability answer, not an error to show
        switchMap((schema) => of(schema)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.schema$;
  }

  /** The names given to types added from the settings screen. */
  private customLabels(): Observable<Record<string, string>> {
    if (!this.labels$) {
      // deferred so a repository that cannot be reached at all — no session yet,
      // or a stubbed client under test — is just "no custom labels"
      this.labels$ = defer(() => {
        const nodes = new NodesApi(this.api.getInstance() as any);
        return this.configFolder.find(LABELS_FILE).pipe(switchMap((id) => from(nodes.getNodeContent(id) as Promise<any>)));
      }).pipe(
        switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? '')))),
        map((text: string) => {
          try {
            const parsed = JSON.parse(text);
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
          } catch {
            return {};
          }
        }),
        catchError(() => of({} as Record<string, string>)),
        tap((labels) => DmsMetadataService.setCustomLabels(labels)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.labels$;
  }

  /**
   * Forgets the cached contract. Called after the model itself is changed, so
   * the next reader sees the types as they now are.
   */
  reset(): void {
    this.schema$ = undefined;
    this.labels$ = undefined;
  }

  /** Uploads one file into a folder and returns the created node. */
  upload(file: File, parentId: string): Observable<any> {
    return from(
      this.uploadApi.uploadFile(file, '', parentId, null, { name: file.name, nodeType: 'cm:content', autoRename: true }) as Promise<any>
    ).pipe(map((res) => res?.entry ?? res));
  }

  /**
   * Writes properties onto a node.
   *
   * Setting a `dms:` property applies {@link DMS_ASPECT} automatically, so the
   * aspect is requested explicitly only to keep the node's existing aspects intact.
   */
  applyMetadata(nodeId: string, properties: Record<string, any>): Observable<any> {
    const clean = Object.fromEntries(Object.entries(properties).filter(([, v]) => v !== null && v !== undefined && v !== ''));
    if (!Object.keys(clean).length) {
      return of(null);
    }

    return this.call(`/nodes/${nodeId}`, 'GET', null, { include: 'aspectNames' }).pipe(
      switchMap((res) => {
        const aspects = new Set<string>(res?.entry?.aspectNames || []);
        if (Object.keys(clean).some((key) => key.startsWith('dms:'))) {
          aspects.add(DMS_ASPECT);
        }
        if (clean['cm:title'] || clean['cm:description']) {
          aspects.add('cm:titled');
        }
        if (clean['cm:to'] || clean['cm:from']) {
          aspects.add('cm:effectivity');
        }
        return this.call(`/nodes/${nodeId}`, 'PUT', { aspectNames: [...aspects], properties: clean });
      })
    );
  }

  /** Adds tags to a node. Alfresco creates any tag that does not exist yet. */
  addTags(nodeId: string, tags: string[]): Observable<any> {
    const clean = tags.map((t) => t.trim()).filter(Boolean);
    if (!clean.length) {
      return of(null);
    }
    return this.call(
      `/nodes/${nodeId}/tags`,
      'POST',
      clean.map((tag) => ({ tag }))
    );
  }

  /** Display text for a constrained value; unknown codes render as themselves. */
  static label(value: string): string {
    return CUSTOM_LABELS[value] || VALUE_LABELS[value] || value;
  }

  /** Takes the names of the types added from the settings screen. */
  static setCustomLabels(labels: Record<string, string>): void {
    CUSTOM_LABELS = labels ?? {};
  }

  private toSchema(properties: any[]): DmsSchema {
    const fields: Record<string, DmsField> = {};

    for (const prop of properties) {
      const list = (prop.constraints || []).find((c: any) => c.type === 'LIST');
      const allowed: string[] = list?.parameters?.allowedValues ?? [];

      fields[prop.id] = {
        id: prop.id,
        dataType: prop.dataType,
        mandatory: !!prop.isMandatoryEnforced,
        defaultValue: prop.defaultValue,
        options: allowed.length ? allowed.map((value) => ({ value, label: DmsMetadataService.label(value) })) : undefined
      };
    }

    return { fields, available: Object.keys(fields).length > 0 };
  }
}
